import React, { FC, memo, useEffect, useMemo, useState } from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import { MultiplyIcon } from '@inno-ui/icons'
import clsx from 'clsx'
import { useNewsTickerQuery } from 'shared/api/productUpdatesApi'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { useUserContext } from 'shared/hooks/useUserContext'
import { lsGetUserItem, lsSetUserItem } from 'shared/utils/local-storage'

import styles from './NewsTickerBanner.module.scss'

// Dismissing the banner snoozes the whole ticker (all messages) for this window,
// matching the product-update modal's 24h snooze. The banner reappears afterwards.
const NEWS_TICKER_SNOOZE_TTL_MS = 24 * 60 * 60 * 1000

// Whether the user's banner-wide dismissal is still within the snooze window.
const isNewsTickerSnoozed = (userId: string): boolean => {
  const dismissedAt = lsGetUserItem(
    LocalStorageKey.NEWS_TICKER_DISMISSED,
    userId
  )
  return (
    typeof dismissedAt === 'number' &&
    Date.now() - dismissedAt < NEWS_TICKER_SNOOZE_TTL_MS
  )
}

export const NewsTickerBanner: FC = memo(() => {
  const { user } = useUserContext()
  const userId = typeof user?.id === 'number' ? `${user.id}` : ''
  const [showBanner, setShowBanner] = useState(false)

  const { data: newsTicker } = useNewsTickerQuery()

  const effectiveNews = useMemo(() => {
    if (isNewsTickerSnoozed(userId)) return []
    return newsTicker ?? []
  }, [newsTicker, userId])

  const handleCloseBanner = () => {
    if (userId) {
      lsSetUserItem(LocalStorageKey.NEWS_TICKER_DISMISSED, userId, Date.now())
    }
    setShowBanner(false)
  }

  useEffect(() => {
    if (effectiveNews.length) {
      setShowBanner(true)
    }
  }, [effectiveNews])

  return (
    <div
      className={clsx(styles.ticker, { [styles.closed]: !showBanner })}
      role='region'
      aria-label='News and updates'
    >
      <div className={styles.inner}>
        <div className={styles.text}>
          <div className={styles.track}>
            <div className={styles.content}>
              {effectiveNews.map(message => (
                <span key={message.id} className={styles.item}>
                  {message.content}
                </span>
              ))}
            </div>
          </div>
        </div>
        <Button
          variant={ButtonVariants.Ghost}
          icon={MultiplyIcon}
          size={ButtonSizes.S}
          onClick={handleCloseBanner}
          aria-label='Close news ticker'
        />
      </div>
    </div>
  )
})

NewsTickerBanner.displayName = 'NewsTickerBanner'
